// Hook: Trade journal CRUD + auto-populate from trades
import { useState, useCallback, useEffect, useMemo } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp, orderBy, query } from 'firebase/firestore';
import { db, auth } from '../firebase';

export function useTradeJournal() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    const ref = collection(db, 'users', user.uid, 'tradeJournal');
    const q = query(ref, orderBy('date', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const addEntry = useCallback(async (entry) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = collection(db, 'users', user.uid, 'tradeJournal');
    return addDoc(ref, {
      ...entry,
      date: entry.date || new Date().toISOString().split('T')[0],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }, []);

  const updateEntry = useCallback(async (id, data) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'tradeJournal', id);
    return updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
  }, []);

  const deleteEntry = useCallback(async (id) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'tradeJournal', id);
    return deleteDoc(ref);
  }, []);

  // Auto-populate from trade book entries
  const createFromTrade = useCallback(async (trade) => {
    return addEntry({
      symbol: trade.tradingsymbol,
      exchange: trade.exchange,
      side: trade.transactiontype,
      quantity: parseInt(trade.quantity),
      entryPrice: parseFloat(trade.price),
      exitPrice: null,
      pnl: null,
      date: new Date().toISOString().split('T')[0],
      notes: '',
      tags: [],
      emotion: 'neutral',
      setup: '',
      mistakes: '',
      lessons: '',
    });
  }, [addEntry]);

  // P&L calendar data
  const calendarData = useMemo(() => {
    const dayMap = {};
    entries.forEach(entry => {
      const date = entry.date;
      if (!dayMap[date]) dayMap[date] = { date, pnl: 0, trades: 0 };
      dayMap[date].pnl += parseFloat(entry.pnl) || 0;
      dayMap[date].trades += 1;
    });
    return Object.values(dayMap);
  }, [entries]);

  // Monthly summary
  const monthlySummary = useMemo(() => {
    const monthMap = {};
    entries.forEach(entry => {
      const month = entry.date?.slice(0, 7);
      if (!month) return;
      if (!monthMap[month]) monthMap[month] = { month, pnl: 0, trades: 0, wins: 0 };
      const pnl = parseFloat(entry.pnl) || 0;
      monthMap[month].pnl += pnl;
      monthMap[month].trades += 1;
      if (pnl > 0) monthMap[month].wins += 1;
    });
    return Object.values(monthMap).sort((a, b) => b.month.localeCompare(a.month));
  }, [entries]);

  return {
    entries,
    loading,
    addEntry,
    updateEntry,
    deleteEntry,
    createFromTrade,
    calendarData,
    monthlySummary,
  };
}
